import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config';
import './ContactModal.css';

const EMPTY = { name: '', email: '', project: '', budget: '' };

function ContactModal({ open, onClose }) {
  const { t } = useTranslation();
  const [data, setData] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStatus(null);
    setData(EMPTY);

    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const change = (e) => {
    const { name, value } = e.target;
    setData(prev => ({ ...prev, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const message = data.budget
      ? `${data.project}\n\n${t('contact.fields.budget')}: ${data.budget}`
      : data.project;

    try {
      const res = await fetch(`${API_BASE_URL}/contact/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          message
        })
      });

      if (res.ok) {
        setStatus('success');
        setData(EMPTY);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="cm-backdrop" onMouseDown={onBackdrop} role="presentation">
      <div
        ref={dialogRef}
        className="cm-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cm-title"
      >
        <button
          type="button"
          className="cm-close"
          aria-label={t('contact.modal.close')}
          onClick={onClose}
        >×</button>

        <div className="cm-accent" />

        {status === 'success' ? (
          <div className="cm-state">
            <h3 id="cm-title">{t('contact.success.title')}</h3>
            <p>{t('contact.success.body')}</p>
            <button type="button" className="cm-submit" onClick={onClose}>
              {t('contact.modal.close')}
            </button>
          </div>
        ) : (
          <>
            <h3 id="cm-title" className="cm-title">{t('contact.modal.title')}</h3>
            <p className="cm-subtitle">{t('contact.modal.subtitle')}</p>

            {status === 'error' && (
              <div className="cm-error">
                <strong>{t('contact.error.title')}</strong> {t('contact.error.body')}
              </div>
            )}

            <form className="cm-form" onSubmit={submit}>
              <label className="cm-field">
                <span>{t('contact.fields.name')}</span>
                <input
                  type="text"
                  name="name"
                  value={data.name}
                  onChange={change}
                  required
                  autoFocus
                />
              </label>

              <label className="cm-field">
                <span>{t('contact.fields.email')}</span>
                <input
                  type="email"
                  name="email"
                  value={data.email}
                  onChange={change}
                  required
                />
              </label>

              <label className="cm-field">
                <span>{t('contact.fields.project')}</span>
                <textarea
                  name="project"
                  rows="5"
                  value={data.project}
                  onChange={change}
                  required
                />
              </label>

              <label className="cm-field">
                <span>{t('contact.fields.budget')}</span>
                <input
                  type="text"
                  name="budget"
                  value={data.budget}
                  onChange={change}
                  placeholder={t('contact.fields.budgetPlaceholder')}
                />
              </label>

              <button type="submit" className="cm-submit" disabled={submitting}>
                {submitting ? t('contact.submit.loading') : t('contact.submit.idle')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default ContactModal;
